import { Fragment, useEffect, useId, useRef } from 'react'
import { Lang, PrismLight } from '@/components/ui'
import { call } from '@/content/site'
import { useInView } from '@/hooks/useInView'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useScrollProgress } from '@/hooks/useScrollProgress'
import { cx } from '@/lib/cx'
import { refreshScroll } from '@/lib/gsap'
import { clamp, damp, lerp, smoothstep } from '@/lib/math'
import { pointer } from '@/lib/pointer'
import { useAppStore } from '@/lib/store'
import styles from './CallStage.module.css'
import { drift, mixRgb, onTick, readRgb, rgba } from './motion'
import { CALL_SECONDS, CALL_TIMELINE } from './timeline'
import { Waveform } from './waveform'

const {
  lines: LINES,
  transitions: TRANSITIONS,
  chips: CHIPS,
  firstWord: FIRST_WORD,
} = CALL_TIMELINE

/** Opacity of the line just spoken, and of older lines, as the captions move on. */
const PAST_OPACITY = 0.34
const FAR_OPACITY = 0.1
/** Unspoken words wait as a 20% ghost and rise this far (em) before they land. */
const GHOST = 0.2
const RISE_EM = 0.32

function formatTime(totalSeconds: number): string {
  const seconds = Math.floor(totalSeconds)
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0')
  const ss = String(seconds % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

/** Mutable animation state that survives the ticker pausing offscreen. */
interface Sim {
  p: number
  ty: number
  level: number
  tint: number
  orb: number
  elapsed: number
  second: number
  orbClock: number
  lightX: number
  lightY: number
  tiltX: number
  tiltY: number
}

/** Props for {@link CallStage}. */
export interface CallStageProps {
  /** Reduced motion: no pin, every line visible, every task done, a still waveform. */
  reduced: boolean
}

/**
 * The pinned call: a frosted-glass call card over soft prism light. Scroll plays the
 * illustrative conversation word by word (romanised Hindi plus English subtitles), the
 * waveform and the prismatic orb follow whoever is speaking, a live timer counts while the
 * card is on screen, and task chips resolve as Vokarta gets things done.
 *
 * Every line is real DOM text in reading order, so screen readers get the whole
 * conversation regardless of what scroll has revealed.
 */
export function CallStage({ reduced }: CallStageProps) {
  const labelId = useId()
  const wrapRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const rigRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLElement>(null)
  const orbRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLOListElement>(null)
  const timerRef = useRef<HTMLSpanElement>(null)
  const sheenRef = useRef<HTMLSpanElement>(null)
  const simRef = useRef<Sim>({
    p: 0,
    ty: Number.NaN,
    level: 0,
    tint: 0,
    orb: 1,
    elapsed: 0,
    second: -1,
    orbClock: 0,
    lightX: Number.NaN,
    lightY: Number.NaN,
    tiltX: 0,
    tiltY: 0,
  })

  // The conversation starts just before the card pins and ends as it unpins.
  const progress = useScrollProgress(wrapRef, { start: 'top 12%', end: 'bottom bottom' })
  const inView = useInView(wrapRef, { rootMargin: '10% 0px' })
  const finePointer = useMediaQuery(MEDIA.hover)
  const desktop = useMediaQuery(MEDIA.desktop)
  const menuOpen = useAppStore((state) => state.menuOpen)
  const live = inView && !reduced && !menuOpen

  // The wrapper's height changes between the pinned and the still layout.
  useEffect(() => {
    refreshScroll()
  }, [reduced])

  // Reduced motion: one still waveform, redrawn only when its box changes.
  useEffect(() => {
    const canvas = canvasRef.current
    const card = cardRef.current
    if (!reduced || !canvas || !card) return
    const wave = new Waveform(canvas, {
      owner: readRgb(card, 'var(--color-fg)'),
      vokarta: readRgb(card, 'var(--color-accent)'),
    })
    const draw = () => {
      wave.resize()
      wave.draw({ level: 0.55, tint: 1, time: 2.2 })
    }
    draw()
    const observer = new ResizeObserver(draw)
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [reduced])

  // Live: one ticker callback drives everything, and only while the stage is in view.
  useEffect(() => {
    const stage = stageRef.current
    const rig = rigRef.current
    const card = cardRef.current
    const orb = orbRef.current
    const canvas = canvasRef.current
    const viewport = viewportRef.current
    const track = trackRef.current
    const timer = timerRef.current
    const sheen = sheenRef.current
    if (!live || !stage || !rig || !card || !orb || !canvas || !viewport || !track || !timer) {
      return
    }

    const lineEls = Array.from(track.querySelectorAll<HTMLElement>('[data-line]'))
    const wordEls = lineEls.map((line) =>
      Array.from(line.querySelectorAll<HTMLElement>('[data-word]')),
    )
    const subEls = lineEls.map((line) => line.querySelector<HTMLElement>('[data-sub]'))
    const chipEls = Array.from(card.querySelectorAll<HTMLElement>('[data-chip]'))
    const stepEls = Array.from(stage.querySelectorAll<HTMLElement>('[data-step]'))
    const haloEls = Array.from(orb.querySelectorAll<HTMLElement>('[data-halo]'))
    const blobEls = Array.from(orb.querySelectorAll<HTMLElement>('[data-blob]'))
    const orbGlass = orb.querySelector<HTMLElement>('[data-glass]')
    const lightEls = Array.from(stage.querySelectorAll<HTMLElement>('[data-light]'))
    const plateEls = Array.from(rig.querySelectorAll<HTMLElement>('[data-plate]'))
    const chipTray = card.querySelector<HTMLElement>('[data-chips]')

    const fg = readRgb(card, 'var(--color-fg)')
    const accentText = readRgb(card, 'var(--color-accent-text)')
    const wave = new Waveform(canvas, { owner: fg, vokarta: readRgb(card, 'var(--color-accent)') })

    // Where the track must sit for each line to rest a little above the viewport's centre.
    let anchors: number[] = []
    let orbSize = 48
    let cardWidth = 1
    let cardHeight = 1
    const measure = () => {
      const viewportHeight = viewport.clientHeight
      // Like a chat: the newest line rests low, earlier lines drift up and fade.
      anchors = lineEls.map((line) => line.offsetTop - (viewportHeight - line.offsetHeight) * 0.66)
      orbSize = orb.offsetWidth
      cardWidth = card.offsetWidth
      cardHeight = card.offsetHeight
      wave.resize()
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(viewport)
    observer.observe(track)
    observer.observe(canvas)
    observer.observe(card)

    const sim = simRef.current
    sim.p = progress.current // re-entering: start where the scroll is, never rewind visibly
    sim.ty = Number.NaN
    const wordCache = wordEls.map((words) => new Float32Array(words.length).fill(-1))
    const lineCache = new Float32Array(lineEls.length).fill(-1)
    const subCache = new Float32Array(lineEls.length).fill(-1)
    const chipCache: string[] = chipEls.map(() => '')
    let stepCache = -1

    const tick = (dt: number, time: number) => {
      // Read layout first, before this frame writes anything.
      const rect = finePointer ? card.getBoundingClientRect() : null

      const target = progress.current
      const lag = target - sim.p
      sim.p = damp(sim.p, target, 4.2, dt)
      const p = sim.p

      // Focus: a float line index that glides across each breath between lines.
      let focus = 0
      for (const transition of TRANSITIONS) focus += smoothstep(transition.start, transition.end, p)
      const current = Math.min(Math.floor(focus), LINES.length - 1)
      const next = Math.min(current + 1, LINES.length - 1)
      const targetY = lerp(anchors[current] ?? 0, anchors[next] ?? 0, focus - current)
      // Wheel notches and touch flings can cross a whole line transition in one frame.
      const ty = Number.isNaN(sim.ty) ? targetY : damp(sim.ty, targetY, 6, dt)
      if (!(Math.abs(ty - sim.ty) < 0.05)) {
        sim.ty = ty
        track.style.transform = `translate3d(0, ${(-ty).toFixed(2)}px, 0)`
      }

      // Lines: arrive softly, then step back as the conversation moves on.
      for (let i = 0; i < lineEls.length; i++) {
        const d = focus - i
        const opacity =
          d <= -1
            ? 0
            : d < 0
              ? smoothstep(-0.7, 0, d)
              : d <= 1
                ? lerp(1, PAST_OPACITY, smoothstep(0, 1, d))
                : lerp(PAST_OPACITY, FAR_OPACITY, clamp(d - 1))
        if (Math.abs(opacity - lineCache[i]) > 0.002) {
          lineCache[i] = opacity
          lineEls[i].style.opacity = opacity.toFixed(3)
        }

        // Words: karaoke. Each rises from a 20% ghost into full ink; Vokarta's pass through iris.
        const cues = LINES[i].words
        const words = wordEls[i]
        const cache = wordCache[i]
        const vokarta = LINES[i].speaker === 'vokarta'
        for (let j = 0; j < words.length; j++) {
          const s = smoothstep(cues[j].start, cues[j].end, p)
          if (Math.abs(s - cache[j]) < 0.0015) continue
          cache[j] = s
          const style = words[j].style
          style.opacity = (GHOST + (1 - GHOST) * s).toFixed(3)
          // 'none', not '': clearing would fall back to the stylesheet's ghost offset.
          style.transform =
            s >= 1 ? 'none' : `translate3d(0, ${((1 - s) * RISE_EM).toFixed(3)}em, 0)`
          const glow = vokarta ? Math.sin(Math.PI * s) : 0
          style.color = glow > 0.01 ? rgba(mixRgb(fg, accentText, glow * 0.9), 1) : ''
        }

        // English subtitle: fades up once the line is under way.
        const sub = subEls[i]
        const t = smoothstep(LINES[i].subtitle.start, LINES[i].subtitle.end, p)
        if (sub && Math.abs(t - subCache[i]) > 0.002) {
          subCache[i] = t
          sub.style.opacity = t.toFixed(3)
          sub.style.transform = t >= 1 ? '' : `translate3d(0, ${((1 - t) * 8).toFixed(2)}px, 0)`
        }
      }

      // Who is speaking, and how much: energetic while scroll moves, a murmur when it rests.
      let speaking = 0
      let tintTarget = sim.tint > 0.5 ? 1 : 0
      for (const line of LINES) {
        const edge = 0.004
        const inside =
          smoothstep(line.speech.start - edge, line.speech.start + edge, p) *
          (1 - smoothstep(line.speech.end - edge, line.speech.end + edge, p))
        if (inside > speaking) {
          speaking = inside
          tintTarget = line.speaker === 'vokarta' ? 1 : 0
        }
      }
      const activity = smoothstep(0, 1, clamp(Math.abs(lag) * 42))
      sim.level = damp(sim.level, speaking * (0.55 + 0.45 * activity), 5, dt)
      sim.tint = damp(sim.tint, tintTarget, 3.2, dt)
      const vokartaVoice = sim.level * sim.tint
      const ringing = 1 - smoothstep(FIRST_WORD * 0.55, FIRST_WORD, p)

      // The prismatic orb: drifts like liquid glass, swells and radiates when Vokarta talks.
      sim.orb = damp(sim.orb, Math.max(ringing * 0.85, vokartaVoice), 4, dt)
      sim.orbClock += dt * (0.45 + 1.5 * sim.orb)
      const swell = 1 + 0.06 * sim.orb * (0.6 + 0.4 * drift(time * 3.1, 2))
      if (orbGlass) orbGlass.style.transform = `scale(${swell.toFixed(4)})`
      for (let i = 0; i < blobEls.length; i++) {
        const x = drift(sim.orbClock * 0.9, i * 2.3) * orbSize * 0.26
        const y = drift(sim.orbClock * 0.8, i * 3.7 + 1) * orbSize * 0.26
        blobEls[i].style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0)`
      }
      for (let i = 0; i < haloEls.length; i++) {
        const u = (time * 0.42 + i / haloEls.length) % 1
        const opacity = (1 - u) ** 1.8 * 0.55 * sim.orb
        haloEls[i].style.transform = `scale(${(1 + u * 1.15).toFixed(4)})`
        haloEls[i].style.opacity = opacity.toFixed(3)
      }

      wave.step(dt, { level: sim.level, tint: sim.tint, time })

      // The call timer counts while the card is on screen.
      sim.elapsed += dt
      const second = Math.floor(sim.elapsed)
      if (second !== sim.second) {
        sim.second = second
        timer.textContent = formatTime(second)
      }

      // Task chips: spinner, then a check, each with a soft CSS transition.
      for (let i = 0; i < chipEls.length; i++) {
        const cue = CHIPS[i]
        const state = !cue ? 'idle' : p >= cue.done ? 'done' : p >= cue.pending ? 'pending' : 'idle'
        if (state !== chipCache[i]) {
          chipCache[i] = state
          chipEls[i].dataset.state = state
        }
      }

      const step = Math.round(focus)
      if (step !== stepCache) {
        stepCache = step
        stepEls.forEach((el, index) => {
          el.dataset.state = index < step ? 'past' : index === step ? 'active' : 'next'
        })
      }

      // Light through glass: a soft highlight follows the cursor (or drifts, on touch).
      let lightX: number
      let lightY: number
      const width = cardWidth
      const height = cardHeight
      if (rect && pointer.lastMove > 0) {
        lightX = clamp(pointer.x - rect.left, -0.15 * width, 1.15 * width)
        lightY = clamp(pointer.y - rect.top, -0.15 * height, 1.15 * height)
      } else {
        lightX = (0.5 + 0.36 * drift(time * 0.16, 3)) * width
        lightY = (0.32 + 0.24 * drift(time * 0.13, 5)) * height
      }
      if (Number.isNaN(sim.lightX)) {
        sim.lightX = lightX
        sim.lightY = lightY
      }
      sim.lightX = damp(sim.lightX, lightX, 3, dt)
      sim.lightY = damp(sim.lightY, lightY, 3, dt)
      if (sheen) {
        sheen.style.transform = `translate3d(${sim.lightX.toFixed(1)}px, ${sim.lightY.toFixed(1)}px, 0)`
      }

      // Depth: card, plates, chips and light shift at different depths with the pointer.
      const aimX = finePointer ? pointer.nx : drift(time * 0.12, 8) * 0.3
      const aimY = finePointer ? pointer.ny : drift(time * 0.1, 11) * 0.3
      sim.tiltX = damp(sim.tiltX, aimX, 2.4, dt)
      sim.tiltY = damp(sim.tiltY, aimY, 2.4, dt)
      const tiltX = sim.tiltX
      const tiltY = sim.tiltY
      if (desktop) {
        card.style.translate = `${(tiltX * 6).toFixed(2)}px ${(-tiltY * 5).toFixed(2)}px`
        plateEls.forEach((plate, index) => {
          const depth = (index + 1) * 7
          plate.style.translate = `${(-tiltX * depth).toFixed(2)}px ${(tiltY * depth * 0.8).toFixed(2)}px`
        })
      }
      if (chipTray && desktop) {
        chipTray.style.translate = `${(tiltX * 14).toFixed(2)}px ${(-tiltY * 11).toFixed(2)}px`
      }
      for (let i = 0; i < lightEls.length; i++) {
        const el = lightEls[i]
        const role = el.dataset.light
        const dx = drift(time * 0.07, i * 1.9) * 7 - tiltX * (2 + i * 0.5)
        const dy = drift(time * 0.06, i * 2.7 + 2) * 6 + tiltY * (1.6 + i * 0.4)
        // Iris light rises with Vokarta's voice; sky light with the owner's.
        const boost =
          role === 'iris'
            ? 0.35 + 0.65 * smoothstep(0, 0.8, vokartaVoice + ringing * 0.5)
            : role === 'sky'
              ? 0.7 + 0.3 * smoothstep(0, 0.8, sim.level * (1 - sim.tint))
              : 1
        el.style.transform = `translate3d(${dx.toFixed(2)}vmin, ${dy.toFixed(2)}vmin, 0)`
        el.style.opacity = boost.toFixed(3)
      }
    }

    const removeTick = onTick(tick)
    return () => {
      removeTick()
      observer.disconnect()
      card.style.translate = ''
      if (chipTray) chipTray.style.translate = ''
    }
  }, [live, desktop, finePointer, progress])

  return (
    <div ref={wrapRef} className={cx(styles.wrap, reduced ? styles.still : styles.live)}>
      <div ref={stageRef} className={styles.stage}>
        <div className={styles.light} aria-hidden="true">
          <PrismLight intensity={1} />
          {/* Two voice lamps over the prism light: sky for the owner, iris for Vokarta. */}
          <span className={cx(styles.lamp, styles.sky)} data-light="sky" />
          <span className={cx(styles.lamp, styles.iris)} data-light="iris" />
        </div>

        <div ref={rigRef} className={styles.rig}>
          <span className={cx(styles.plate, styles.plateFar)} data-plate aria-hidden="true" />
          <span className={cx(styles.plate, styles.plateNear)} data-plate aria-hidden="true" />

          <article ref={cardRef} className={styles.card} aria-labelledby={labelId}>
            <span className={styles.sheen} aria-hidden="true">
              <span ref={sheenRef} className={styles.sheenSpot} />
            </span>

            <header className={styles.head}>
              <div ref={orbRef} className={styles.orb} aria-hidden="true">
                <span className={styles.halo} data-halo />
                <span className={styles.halo} data-halo />
                <span className={styles.orbGlass} data-glass>
                  <span className={cx(styles.orbBlob, styles.orbLilac)} data-blob />
                  <span className={cx(styles.orbBlob, styles.orbSky)} data-blob />
                  <span className={cx(styles.orbBlob, styles.orbMint)} data-blob />
                  <span className={styles.orbSpec} />
                </span>
              </div>
              <div className={styles.callerBlock}>
                <p className={styles.caller}>{call.caller}</p>
                <p className={styles.status} aria-hidden="true">
                  <span className={styles.liveDot} />
                  <span ref={timerRef} className={cx('tabular', styles.timer)}>
                    {formatTime(reduced ? CALL_SECONDS : 0)}
                  </span>
                </p>
              </div>
              <p id={labelId} className={styles.label}>
                {call.label}
              </p>
            </header>

            <div className={styles.wave} aria-hidden="true">
              <canvas ref={canvasRef} className={styles.canvas} />
            </div>

            <div ref={viewportRef} className={styles.captions}>
              <ol ref={trackRef} className={styles.track}>
                {LINES.map((line, index) => {
                  const dialogue = call.dialogue[index]
                  return (
                    <li key={index} className={styles.line} data-line data-speaker={line.speaker}>
                      <p className={styles.who}>
                        <span className={styles.whoMark} aria-hidden="true" />
                        {call.speakers[line.speaker]}
                      </p>
                      <p className={styles.hi}>
                        <Lang code="hi-Latn" className="sr-only">
                          {dialogue.hi}
                        </Lang>
                        <span lang="hi-Latn" aria-hidden="true">
                          {line.tokens.map((token, wordIndex) => (
                            <Fragment key={wordIndex}>
                              {wordIndex > 0 ? ' ' : null}
                              <span className={styles.word} data-word>
                                {token}
                              </span>
                            </Fragment>
                          ))}
                        </span>
                      </p>
                      <p className={styles.en} data-sub>
                        {dialogue.en}
                      </p>
                    </li>
                  )
                })}
              </ol>
            </div>

            <ul className={styles.chips} data-chips>
              {call.tasks.map((task) => (
                <li
                  key={task}
                  className={styles.chip}
                  data-chip
                  data-state={reduced ? 'done' : 'idle'}
                >
                  <span className={styles.chipIcon} aria-hidden="true">
                    <span className={styles.spinner} />
                    <svg className={styles.check} viewBox="0 0 24 24" focusable="false">
                      <path d="M7 12.6l3.3 3.2L17.2 9" />
                    </svg>
                  </span>
                  <span className={styles.chipText}>{task}</span>
                </li>
              ))}
            </ul>
          </article>
        </div>

        <div className={styles.steps} aria-hidden="true">
          {LINES.map((line, index) => (
            <span key={index} className={styles.step} data-step data-speaker={line.speaker} />
          ))}
        </div>
      </div>
    </div>
  )
}
